// ---------------------------------------------------------------------------
// Inlined from ./api.ts
// ---------------------------------------------------------------------------

import { z } from 'zod';

export const errorCodeSchema = z.enum([
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION_ERROR',
  'INSUFFICIENT_CREDITS',
  'RATE_LIMITED',
  'GENERATION_FAILED',
  'PAYMENT_REQUIRED',
  'INTERNAL_ERROR',
]);

export const apiErrorSchema = z.object({
  success: z.literal(false),
  error: z.object({
    code: errorCodeSchema,
    message: z.string(),
    details: z.unknown().optional(),
  }),
});

// ---------------------------------------------------------------------------
// Inlined from ./category.ts
// ---------------------------------------------------------------------------

export const categorySchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable().optional(),
  coverImageUrl: z.string().nullable().optional(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  promptCount: z.number().int().optional(),
});

// ---------------------------------------------------------------------------
// Inlined from ./prompt.ts
// ---------------------------------------------------------------------------

export const aspectRatioSchema = z.enum(['1:1', '4:5', '3:4', '16:9', '9:16', '2:3', '3:2']);
export type AspectRatio = z.infer<typeof aspectRatioSchema>;

export const promptSortSchema = z.enum([
  'newest',
  'popular',
  'trending',
  'most_copied',
  'most_viewed',
]);
export type PromptSort = z.infer<typeof promptSortSchema>;

export const modelSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  provider: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
});

export const tagSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
});

export const promptImageSchema = z.object({
  id: z.string(),
  imageUrl: z.string(),
  thumbnailUrl: z.string(),
  width: z.number().int().nullable().optional(),
  height: z.number().int().nullable().optional(),
  // LQIP data URL for instant placeholder rendering. Repurposes the
  // legacy `blurhash` column name to avoid a migration.
  blurhash: z.string().nullable().optional(),
  altText: z.string().nullable().optional(),
  isPrimary: z.boolean(),
});

export const promptCardSchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string(),
  thumbnailUrl: z.string(),
  imageUrl: z.string(),
  width: z.number().int().nullable().optional(),
  height: z.number().int().nullable().optional(),
  category: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
  }),
  model: modelSchema.nullable(),
  tags: z.array(tagSchema),
  viewCount: z.number().int(),
  copyCount: z.number().int(),
  saveCount: z.number().int(),
  aspectRatio: z.string().nullable().optional(),
  createdAt: z.string(),
});

export const promptDetailSchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string(),
  promptLocked: z.boolean(),
  promptText: z.string().nullable(),
  negativePrompt: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  category: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
  }),
  model: modelSchema.nullable(),
  tags: z.array(tagSchema),
  images: z.array(promptImageSchema),
  aspectRatio: z.string().nullable().optional(),
  style: z.string().nullable().optional(),
  viewCount: z.number().int(),
  copyCount: z.number().int(),
  saveCount: z.number().int(),
  generationCount: z.number().int(),
  publishedAt: z.string().nullable().optional(),
  createdAt: z.string(),
});

// ---------------------------------------------------------------------------
// Inlined from ./showcase.ts
// ---------------------------------------------------------------------------

export const MAX_SHOWCASE_ITEMS = 10;

export const showcaseItemSchema = z.object({
  id: z.string(),
  generationJobId: z.string(),
  order: z.number(),
  referenceImageKey: z.string().nullable(),
  outputImageKey: z.string().nullable(),
  inputPrompt: z.string(),
  model: z.string().nullable(),
  provider: z.string().nullable(),
  aspectRatio: z.string().nullable(),
  completedAt: z.string().nullable(),
});

export type ShowcaseItem = z.infer<typeof showcaseItemSchema>;

export const MAX_HERO_PROMPTS = 4;
